import { ReactNode, useEffect } from 'react';
import { TaskChooseOrganization, useAuth as useClerkAuth, useOrganization } from '@clerk/react';

import { readPendingCompanyName, readPendingTenantId } from '@/api/tenants/linkClerkOrg';
import Loader from '@/component-library/primitives/Loader/Loader';
import PrefillCreateOrganization from '@/component-library/widgets/PrefillCreateOrganization/PrefillCreateOrganization';
import { useResolvedPendingCompanyName } from '@/hooks/useResolvedPendingCompanyName';
import { isClerkAuthEnabled } from '@/utils/auth/authMode';
import { isPathBCustomOrgConfirmEnabled } from '@/utils/auth/pathBOrgConfirm';
import { AppRoutes } from '@/utils/routes/router';

const LOG = '[ChurnSignal][PathB-Org]';

type Props = {
  children: ReactNode;
};

const ClerkOrgGate = ({ children }: Props) => {
  if (!isClerkAuthEnabled()) {
    return <>{children}</>;
  }

  return <ClerkOrgGateInner>{children}</ClerkOrgGateInner>;
};

const ClerkOrgGateInner = ({ children }: Props) => {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { organization, isLoaded: isOrgLoaded } = useOrganization();
  const customEnabled = isPathBCustomOrgConfirmEnabled();
  const { companyName, isResolving, hasPendingRegistration } = useResolvedPendingCompanyName();

  useEffect(() => {
    if (!isLoaded || !isOrgLoaded) {
      return;
    }
    console.info(LOG, 'ClerkOrgGate', {
      mode: customEnabled ? 'custom' : 'clerk',
      isSignedIn,
      organizationId: organization?.id ?? null,
      hasPendingRegistration,
      pendingTenantId: readPendingTenantId(),
      pendingCompanyNameStorage: readPendingCompanyName(),
      willRender:
        isSignedIn && !organization
          ? customEnabled && hasPendingRegistration
            ? isResolving
              ? 'loading'
              : 'PrefillCreateOrganization'
            : 'TaskChooseOrganization (Clerk)'
          : 'children (dashboard)',
    });
  }, [
    isLoaded,
    isOrgLoaded,
    isSignedIn,
    organization?.id,
    customEnabled,
    hasPendingRegistration,
    isResolving,
    companyName,
  ]);

  if (!isLoaded || !isOrgLoaded) {
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (isSignedIn && !organization) {
    if (customEnabled && hasPendingRegistration) {
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 py-12">
          {isResolving ? <Loader /> : <PrefillCreateOrganization initialName={companyName} />}
        </div>
      );
    }

    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center px-4 py-12">
        <p className="mb-6 max-w-md text-center text-custom-darkSilver">
          Choose or create an organization to continue. Each organization maps to one ChurnSignal tenant.
        </p>
        <TaskChooseOrganization redirectUrlComplete={AppRoutes.dashboard} />
      </div>
    );
  }

  return <>{children}</>;
};

export default ClerkOrgGate;
